import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type ShouldRevalidateFunction } from 'react-router';
import type { CapiPlp, GridSize } from 'types/capi/plp';
import * as uuid from 'uuid';

import Content from '~/components/Content';
import Paragraphs from '~/components/Content/Paragraphs';
import DownloadAppBlock, {
  HOMEPAGE_APP_DOWNLOAD_URL,
  HOMEPAGE_APP_QR_CODE_URL
} from '~/components/DownloadAppBlock';
import PageHeader from '~/components/PageHeader';
import PopularReleasesCarousel from '~/components/PopularReleasesCarousel';
import RecommendedArticles from '~/components/RecommendedArticles';
import ScrollFade from '~/components/ScrollFade';
import PlpViewEvent from '~/events/PlpViewEvent';
import SearchViewEvent from '~/events/SearchViewEvent';
import usePageData from '~/hooks/usePageData';
import useRootData from '~/hooks/useRootData';
import useStructuredData from '~/hooks/useStructuredData';
import useUser from '~/hooks/useUser';
import useViewEvent from '~/hooks/useViewEvent';
import ProductCardCarousel from '~/routes/pdps/affiliate/ProductCardCarousels/ProductCardCarousel';
import gtm from '~/services/gtm.client';
import { isSignInAction } from '~/utils/signInAction';

import Advert from './components/Advert';
import FilterOptions from './components/FilterOptions';
import Menus from './components/Menus/Menus';
import { TopModuleSection } from './components/ModuleSection';
import NoResults from './components/NoResults';
import NotificationTooltip from './components/NotificationTooltip';
import Products from './components/Products';
import SubMenu from './components/SubMenu';
import {
  getPlpModulePositions,
  getPlpViewProperties,
  getRecentArticlesHeading,
  getVisibleBreadcrumbs,
  POPULAR_CAROUSEL_MODULE_TYPE,
  showsNoResults,
  showsWhatsHot,
  WHATS_HOT_MODULE_TYPE
} from './plpViewProperties';

function FilterBar({
  filterOptions,
  menus
}: {
  filterOptions: CapiPlp['filter_options'];
  menus: CapiPlp['menus'];
}) {
  const hasMenus = menus.length > 0;
  const hasFilterOptions = filterOptions.length > 1;

  if (!hasMenus && !hasFilterOptions) return null;

  return (
    <ScrollFade
      className="flex h-full items-center gap-2 px-4 md:px-8 desktop:px-0"
      fadeClassName="from-grey-tile desktop:hidden"
      wrapperClassName="-mx-4 mt-auto h-full md:-mx-8 desktop:mx-0"
    >
      {hasFilterOptions && <FilterOptions options={filterOptions} />}
      {hasMenus && hasFilterOptions && (
        <span className="shrink-0 px-1 text-grey-hide">•</span>
      )}
      {hasMenus && <Menus menus={menus} />}
    </ScrollFade>
  );
}

export default function PlpView({
  allowNotifications = true,
  offersWaysForward = false,
  showAppBanner = true
}: {
  allowNotifications?: boolean;
  offersWaysForward?: boolean;
  showAppBanner?: boolean;
  showPromotion?: boolean;
}) {
  const plp = usePageData<CapiPlp>();
  const { t } = useTranslation(['plp', 'common']);

  const isUpcoming = plp.filter_options.some(
    (o) => o.text === 'Upcoming' && o.active
  );
  const noResults = showsNoResults(plp, offersWaysForward);
  const [rootPath = '/'] = plp.path_root.split('?');
  const modulePositions = getPlpModulePositions(plp, offersWaysForward);

  const user = useUser();
  const { clientIp, isBot, session } = useRootData();
  const [gridSize, setGridSize] = useState<GridSize>('small');

  const [introContent] = plp.content ?? [];
  const introParagraphs =
    introContent?.type === 'paragraphs' ? introContent : undefined;
  const bottomParagraphs = plp.content?.slice(introParagraphs ? 1 : 0);
  const intro =
    plp.sub_title || introParagraphs ? (
      <>
        {plp.sub_title && <p className="flex text-sm">{plp.sub_title}</p>}
        {introParagraphs && (
          <div className="hidden text-grey-read md:block lg:w-3/4">
            <Paragraphs collapsible paragraphs={introParagraphs} size="small" />
          </div>
        )}
      </>
    ) : undefined;

  const popularReleases = plp.popular_products && (
    <PopularReleasesCarousel
      products={plp.popular_products}
      title="Most popular releases"
      description="The pairs with most notifications set in the last 7 days"
      modulePosition={modulePositions.popular}
      moduleType={POPULAR_CAROUSEL_MODULE_TYPE}
      pagePosition="PLP Popular Carousel"
      pageType="plp"
      allowNotifications
      compact={noResults}
      showBackground
      viewMoreUrl={noResults ? `${rootPath}?sort=UPCOMING_RELEASES` : undefined}
    />
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const listId = useMemo(() => uuid.v4(), [plp]);

  useStructuredData(plp._schemas);

  useViewEvent({
    viewProperties: useMemo(
      () => getPlpViewProperties(plp, offersWaysForward),
      [plp, offersWaysForward]
    ),
    viewType: 'plp'
  });

  useEffect(() => {
    const isSearch = new URLSearchParams(window.location.search).has('q');

    const eventData = {
      clientIp: clientIp ?? undefined,
      isBot,
      listId,
      plp,
      user
    };

    const viewEvent = isSearch
      ? new SearchViewEvent(eventData)
      : new PlpViewEvent(eventData);

    viewEvent.send();
  }, [clientIp, plp, session, user, isBot, listId]);

  return (
    <>
      {plp.advert && <Advert advert={plp.advert} />}
      <TopModuleSection />
      <div className="flex w-full flex-col overflow-x-clip bg-grey-tile">
        <PageHeader
          actions={
            <FilterBar filterOptions={plp.filter_options} menus={plp.menus} />
          }
          breadcrumbs={getVisibleBreadcrumbs(plp)}
          hasFilters
          stackActions
          title={plp.title}
        >
          {intro}
        </PageHeader>
        <div className="content-width w-full">
          {noResults ? (
            <NoResults />
          ) : (
            <>
              <div
                className={`flex flex-col gap-4 pt-3 ${gridSize === 'large' ? 'md:flex-row md:items-center' : 'xs:flex-row xs:items-center'}`}
              >
                {isUpcoming && allowNotifications && (
                  <NotificationTooltip gridSize={gridSize} />
                )}
                <SubMenu setGridSize={setGridSize} gridSize={gridSize} />
              </div>
              <Products
                allowNotifications={allowNotifications}
                feature={popularReleases}
                gridSize={gridSize}
                listId={listId}
                modulePosition={modulePositions.mainList}
              />
            </>
          )}
        </div>
      </div>
      <div className="bg-concrete">
        {plp.featured_products && showsWhatsHot(plp, offersWaysForward) ? (
          <div className="content-width py-8 desktop:py-10">
            <ProductCardCarousel
              allowNotifications={allowNotifications}
              id="featured-products"
              modulePosition={modulePositions.whatsHot}
              moduleType={WHATS_HOT_MODULE_TYPE}
              padImages
              pagePosition="PLP Whats Hot Carousel"
              pageType="plp"
              products={plp.featured_products}
              title={t('sortWhatsHot')}
              trackingName="What's hot"
              viewMoreCopy={t('common:viewMore')}
              onViewMoreProductsClick={() =>
                gtm.sendUserInteractionEvent(
                  "What's Hot View More Drops",
                  'Body'
                )
              }
              viewMoreUrl={plp.featured_products_path ?? rootPath}
            />
          </div>
        ) : null}
        {noResults && popularReleases}
        {plp.recent_articles && (
          <RecommendedArticles
            articles={plp.recent_articles}
            heading={getRecentArticlesHeading(plp)}
            viewMoreNewsPath={plp.view_more_news_path}
          />
        )}
        {bottomParagraphs && bottomParagraphs.length ? (
          <div className="content-width flex flex-col gap-8 py-8 lg:py-12 [&_p]:text-sm [&_p]:text-grey-read">
            <Content content={bottomParagraphs} />
          </div>
        ) : null}
      </div>
      {showAppBanner && (
        <DownloadAppBlock
          downloadLink={HOMEPAGE_APP_DOWNLOAD_URL}
          qrCode={HOMEPAGE_APP_QR_CODE_URL}
          pagePosition="PLP App Banner"
        />
      )}
    </>
  );
}

export const shouldRevalidate: ShouldRevalidateFunction = ({
  defaultShouldRevalidate,
  formAction
}) => {
  if (isSignInAction(formAction)) return true;
  if (formAction?.startsWith('/bolt')) return false;

  return defaultShouldRevalidate;
};
